import { useAppSelector } from '../../../hooks'
import { getEditableInventoryCentres } from '../../product/productInventory'
import { useFormik } from 'formik'
import React, { useState } from 'react'
import { NumericFormat } from 'react-number-format'
import ModalComponent from '..'
import {
  formatCurrency,
  getCurrencySymbol,
  parseCurrencyToNumber,
} from '../../../helpers'
import {
  useGetProductInventoryQuery,
  useUpdateItemStockMutation,
} from '../../../redux-toolkits/products/products.slice'
import { UpdateProductStockPayload } from '../../../redux-toolkits/products/products.type'
import { FulfilmentCentre } from '../../../redux-toolkits/fulfilment-centres/fulfilmentCentres.type'
import {
  Product,
  VariantCombination,
} from '../../../redux-toolkits/products/products.type'
import {
  UpdateProductPriceSchema,
  UpdateProductSchema,
} from '../../../validation/validate'
import { Button } from '../../button'
import Icon from '../../icon'
import Dropdown from '../../inputs/dropdown'
import { TextInput } from '../../inputs/textInput'
import { TabNav } from '../../nav/tabNav'

const reasons = [
  {
    name: 'Inventory re-count',
    value: 'Inventory re-count',
  },
  {
    name: 'Damage',
    value: 'Damage',
  },
  {
    name: 'Loss',
    value: 'Loss',
  },
  {
    name: 'Stock received',
    value: 'Stock received',
  },
  {
    name: 'Others',
    value: 'Others',
  },
  {
    name: 'Restock return',
    value: 'Restock return',
  },
  {
    name: 'Theft',
    value: 'Theft',
  },
]

export const UpdateItemStockModal: React.FC<{
  open: boolean
  setOpen: (cal: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: (adjustment?: {
    fulfilmentCentreId?: string
    variantCombinationId?: string
    quantityDelta: number
  }) => void
  product?: Product
  fulfilmentCentres?: FulfilmentCentre[]
  variantCombination?: VariantCombination
  type?: 'price' | 'quantity'
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = () => {},
  product,
  fulfilmentCentres = [],
  variantCombination,
  type = 'quantity',
}) => {
  const { user } = useAppSelector((state) => state.auth)
  const editableCentres = getEditableInventoryCentres(
    fulfilmentCentres,
    user?.fulfillmentCenter,
  )
  const [updateStock, { isLoading }] = useUpdateItemStockMutation()
  const usesFulfilmentCentreInventory =
    product?.inventoryMode === 'FULFILMENT_CENTRE'
  const { data: inventory } = useGetProductInventoryQuery(
    { id: product?._id ?? '' },
    {
      skip:
        !open ||
        !product?._id ||
        type !== 'quantity' ||
        !usesFulfilmentCentreInventory,
    },
  )

  const [quantityType, setQuantityType] = useState('increase')

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    handleBlur,
    touched,
    isValid,
    resetForm,
    setFieldValue,
  } = useFormik({
    initialValues: {
      reason: {
        name: '',
        value: '',
      },
      quantity: '',
      price: '',
      fulfilmentCentreId: '',
    },
    enableReinitialize: true,
    validationSchema:
      type === 'price' ? UpdateProductPriceSchema : UpdateProductSchema,
    onSubmit: (values, { resetForm }) => {
      if (
        type === 'quantity' &&
        usesFulfilmentCentreInventory &&
        !editableCentres.some(({ _id }) => _id === values.fulfilmentCentreId)
      )
        return
      const payload: UpdateProductStockPayload =
        type === 'price'
          ? {
              adjustInventory: false,
              price: parseCurrencyToNumber(values.price),
              adjustmentReason: values.reason.value,
            }
          : {
              adjustInventory: true,
              adjustedQuantity: Number(values.quantity),
              adjustmentReason: values.reason.value,
              increaseStock: quantityType === 'increase',
              fulfilmentCentreId: usesFulfilmentCentreInventory
                ? values.fulfilmentCentreId
                : undefined,
              variantCombinationId: variantCombination?._id,
            }

      updateStock({ id: product?._id ?? '', payload }).then((resp) => {
        if (resp.data?.success) {
          onSuccessCallback({
            fulfilmentCentreId: values.fulfilmentCentreId || undefined,
            variantCombinationId: variantCombination?._id,
            quantityDelta:
              type === 'quantity'
                ? Number(values.quantity) *
                  (quantityType === 'increase' ? 1 : -1)
                : 0,
          })
          resetForm()
        }
      })
    },
  })

  const selectedCentreInventory = inventory?.data.fulfilmentCentres.find(
    ({ fulfilmentCentreId }) =>
      fulfilmentCentreId === values.fulfilmentCentreId,
  )
  const currentQuantity = usesFulfilmentCentreInventory
    ? variantCombination?._id
      ? selectedCentreInventory?.variants.find(
          ({ variantCombinationId }) =>
            variantCombinationId === variantCombination._id,
        )?.quantity ?? 0
      : selectedCentreInventory?.quantity ?? 0
    : variantCombination?.inventoryCount ?? product?.inStockQuantity ?? 0

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        resetForm()
        onCloseCallback()
      }}
      title={`Update Product ${type === 'price' ? 'Price' : 'Stock'}`}
      childrenClassName="md:!max-w-[420px]"
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4">
          {type === 'quantity' && (
            <div className="font-medium">
              Current quantity in stock:{' '}
              {usesFulfilmentCentreInventory && !values.fulfilmentCentreId
                ? 'Select a fulfilment centre'
                : currentQuantity.toLocaleString()}
            </div>
          )}
          {type === 'price' && (
            <div className="font-medium">
              Current price: {formatCurrency(product?.price ?? 0)}
            </div>
          )}

          {type === 'quantity' &&
            (usesFulfilmentCentreInventory ? (
              <Dropdown
                className="w-full"
                menuClassName="!max-h-[220px]"
                menuButton={
                  <TextInput
                    title="Fulfilment centre"
                    name="fulfilmentCentreId"
                    placeholder="Select fulfilment centre"
                    value={
                      editableCentres.find(
                        ({ _id }) => _id === values.fulfilmentCentreId,
                      )?.name ?? ''
                    }
                    readOnly
                    hasIcon={false}
                    isDropdown
                  />
                }
                menuItems={editableCentres.map((centre) => ({
                  name: centre.name,
                  value: centre._id,
                  onClick: () => {
                    setFieldValue('fulfilmentCentreId', centre._id)
                  },
                }))}
              />
            ) : undefined)}

          {type === 'quantity' && (
            <div className="">
              <TabNav
                activeTab={quantityType}
                setActiveTab={(selected) => {
                  setQuantityType(selected)
                }}
                tabs={[
                  {
                    name: 'Stock increase',
                    id: 'increase',
                  },
                  {
                    name: 'Stock decrease',
                    id: 'decrease',
                  },
                ]}
                type="nav"
              />
            </div>
          )}
          {type === 'price' && (
            <NumericFormat
              suffix={undefined}
              prefix={getCurrencySymbol('NGN')}
              decimalScale={2}
              thousandSeparator
              customInput={TextInput}
              type="text"
              name="price"
              placeholder="New price"
              value={values.price}
              onBlur={handleBlur}
              onChange={handleChange}
              errors={errors.price && touched.price ? errors.price : ''}
              hasIcon={false}
              title="New price"
            />
          )}

          {type === 'quantity' && (
            <TextInput
              type="text"
              name="quantity"
              placeholder={`${quantityType[0].toLocaleUpperCase()}${quantityType.slice(
                1,
              )} stock by (quantity)`}
              value={String(values.quantity)}
              onBlur={handleBlur}
              onChange={handleChange}
              errors={
                errors.quantity && touched.quantity ? errors.quantity : ''
              }
              hasIcon={false}
              title="Quantity"
            />
          )}

          <Dropdown
            className="w-full"
            menuClassName="!max-h-[200px]"
            menuButton={
              <TextInput
                type="text"
                name="reason"
                placeholder="Adjustment reason"
                value={values.reason.name}
                onBlur={handleBlur}
                onChange={handleChange}
                errors={
                  errors.reason?.value && touched.reason?.value
                    ? errors.reason.value
                    : ''
                }
                hasIcon={false}
                readOnly
                extra={
                  <Icon
                    id={'left-caret'}
                    width={16}
                    height={16}
                    className="-rotate-90 text-sec-black"
                  />
                }
                className="!border-divider-gray !w-full"
                title="Adjustment reason"
              />
            }
            menuItems={reasons.map((reason) => ({
              ...reason,
              onClick: () => {
                setFieldValue('reason', reason)
              },
            }))}
          />
        </div>

        <Button
          label="Save Changes"
          type="button"
          className="mt-10"
          disabled={
            !isValid ||
            isLoading ||
            (type === 'quantity' &&
              usesFulfilmentCentreInventory &&
              !editableCentres.some(
                ({ _id }) => _id === values.fulfilmentCentreId,
              ))
          }
          loading={isLoading}
          primary
          onClick={() => {
            handleSubmit()
          }}
        />
      </div>
    </ModalComponent>
  )
}
